import { useCallback, useState } from "react";
import { getCompany, getUser } from "../../data/storage/authStorage";

export interface SortState {
  by: string;
  order: string;
  label: string;
}

const STORAGE_PREFIX = "zournal.sort";

const getStorageScope = (): string => {
  try {
    const user = getUser();
    const company = getCompany();
    const userId = user?.id || "anon";
    const companyId = company?.companyId || "no-company";
    return `${userId}.${companyId}`;
  } catch {
    return "default";
  }
};

const buildKey = (sectionKey: string): string =>
  `${STORAGE_PREFIX}.${getStorageScope()}.${sectionKey}`;

const isSortState = (value: unknown): value is SortState => {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.by === "string" &&
    typeof v.order === "string" &&
    typeof v.label === "string"
  );
};

const readPersistedSort = <T extends SortState>(
  sectionKey: string,
  fallback: T,
): T => {
  try {
    const raw = localStorage.getItem(buildKey(sectionKey));
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return isSortState(parsed) ? (parsed as T) : fallback;
  } catch {
    return fallback;
  }
};

export const usePersistedSort = <T extends SortState>(
  sectionKey: string,
  defaultSort: T,
): [T, (next: T) => void] => {
  const [sort, setSortState] = useState<T>(() =>
    readPersistedSort(sectionKey, defaultSort),
  );

  const setSort = useCallback(
    (next: T) => {
      setSortState(next);
      try {
        localStorage.setItem(buildKey(sectionKey), JSON.stringify(next));
      } catch {
        // Ignore storage errors (quota exceeded, private mode, disabled storage)
      }
    },
    [sectionKey],
  );

  return [sort, setSort];
};
